import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';

interface MediaSettingsContextValue {
  soundEnabled: boolean;
  setSoundEnabled: (enabled: boolean) => void;
  toggleSound: () => void;
  currentPlayingVideo: string | null;
  setCurrentPlayingVideo: (id: string) => void;
}

const MediaSettingsContext = createContext<MediaSettingsContextValue | undefined>(undefined);

const STORAGE_KEY = 'mediaSoundEnabled';

export const MediaSettingsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [soundEnabled, setSoundEnabledState] = useState<boolean>(false);
  const [currentPlayingVideo, setCurrentPlayingVideoState] = useState<string | null>(null);

  // Initialize from sessionStorage (default muted/false)
  useEffect(() => {
    try {
      const saved = sessionStorage.getItem(STORAGE_KEY);
      if (saved === 'true' || saved === 'false') {
        setSoundEnabledState(saved === 'true');
      }
    } catch {
      // Ignore storage errors
    }
  }, []);

  // Persist to sessionStorage
  useEffect(() => {
    try {
      sessionStorage.setItem(STORAGE_KEY, soundEnabled ? 'true' : 'false');
    } catch {
      // Ignore storage errors
    }
  }, [soundEnabled]);

  const setSoundEnabled = (enabled: boolean) => setSoundEnabledState(enabled);
  const toggleSound = () => setSoundEnabledState(prev => !prev);

  // Simple video management - just track current playing video ID
  const setCurrentPlayingVideo = (id: string) => {
    setCurrentPlayingVideoState(id);
  };

  const value = useMemo<MediaSettingsContextValue>(() => ({
    soundEnabled,
    setSoundEnabled,
    toggleSound,
    currentPlayingVideo,
    setCurrentPlayingVideo
  }), [soundEnabled, currentPlayingVideo]);

  return (
    <MediaSettingsContext.Provider value={value}>
      {children}
    </MediaSettingsContext.Provider>
  );
};

export const useMediaSettings = (): MediaSettingsContextValue => {
  const ctx = useContext(MediaSettingsContext);
  if (!ctx) {
    throw new Error('useMediaSettings must be used within a MediaSettingsProvider');
  }
  return ctx;
};


